import React, { useEffect, useId, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useServerLivesCountdown } from '../hooks/useServerLivesCountdown';
import type { LivesStatus, User } from '../services/api';
import { MAX_LIFE_UNITS, unitsToHeartSlots, type HeartSlotState } from '../utils/livesUnits';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import PulseBadge from './shared/PulseBadge';
import './LivesDisplay.css';

export function readCachedUserType(): User['userType'] | undefined {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return undefined;
    const u = JSON.parse(raw) as Partial<User>;
    return u.userType;
  } catch {
    return undefined;
  }
}

/** Admin only — bypasses life consumption and shows unlimited badge. */
export function isUnlimitedUserType(userType: User['userType'] | undefined): boolean {
  if (userType === undefined || userType === null) return false;
  if (userType === 'Admin' || userType === 3) return true;
  return false;
}

/** Paid VIP (and legacy Premium): double life capacity + faster regen — not unlimited. */
export function isVipDoubleLivesUserType(userType: User['userType'] | undefined): boolean {
  if (userType === undefined || userType === null) return false;
  if (userType === 'Vip' || userType === 'Premium' || userType === 2) return true;
  return false;
}

export interface LivesDisplayProps {
  lives: number;
  livesStatus?: LivesStatus | null;
  userType?: User['userType'];
  variant?: 'sidebar' | 'compact';
  className?: string;
  onCountdownElapsed?: () => void;
  /** AC2: subtle pulse when quiz session starts (solo start) */
  pulseOnMount?: boolean;
}

/** Um coração cheio — mesmas classes dos slots em LivesDisplay (custo, rótulos). */
export function InlineFullHeartIcon({ className = '' }: { className?: string }) {
  return (
    <i
      className={`bi bi-heart-fill lives-ui-heart lives-ui-heart--full ${className}`.trim()}
      aria-hidden
    />
  );
}

/** Meio coração — mesmo markup do slot “half” em LivesDisplay. */
export function InlineHalfHeartIcon({ className = '' }: { className?: string }) {
  return (
    <span className={`lives-ui-heart-half-wrap ${className}`.trim()} aria-hidden title="half">
      <i className="bi bi-heart lives-ui-heart lives-ui-heart--half-bg" />
      <i className="bi bi-heart-fill lives-ui-heart lives-ui-heart--half-fill" />
    </span>
  );
}

function HeartSlot({ state }: { state: HeartSlotState }) {
  if (state === 'full') {
    return <InlineFullHeartIcon />;
  }
  if (state === 'empty') {
    return <i className="bi bi-heart lives-ui-heart lives-ui-heart--empty" aria-hidden />;
  }
  return <InlineHalfHeartIcon />;
}

const LivesDisplay: React.FC<LivesDisplayProps> = ({
  lives,
  livesStatus,
  userType,
  variant = 'sidebar',
  className = '',
  onCountdownElapsed,
  pulseOnMount = false,
}) => {
  const { t } = useSafeTranslation();
  const tooltipId = useId().replace(/:/g, '');
  const onElapsedRef = useRef(onCountdownElapsed);
  onElapsedRef.current = onCountdownElapsed;

  const countdownLabel = useServerLivesCountdown(livesStatus, () => onElapsedRef.current?.());

  const unlimited = isUnlimitedUserType(userType);
  const maxUnits = livesStatus?.maxLives ?? MAX_LIFE_UNITS;
  const showVipX2 = isVipDoubleLivesUserType(userType) && !unlimited;

  /** Admin unlimited: sempre cheio; demais: slots conforme max do tier. */
  const slots = unlimited
    ? unitsToHeartSlots(MAX_LIFE_UNITS, MAX_LIFE_UNITS)
    : unitsToHeartSlots(lives, maxUnits);

  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    if (!pulseOnMount) return;
    setPulse(true);
    const tid = window.setTimeout(() => setPulse(false), 650);
    return () => window.clearTimeout(tid);
  }, [pulseOnMount]);

  const rootClass =
    variant === 'compact'
      ? 'lives-ui lives-ui--compact'
      : 'lives-ui lives-ui--sidebar';

  const x2Tooltip = (
    <Tooltip id={`lives-vip-x2-${tooltipId}`}>
      {t('user.vipLivesDoubleTooltip', 'VIP benefit: Double lives & faster recharge')}
    </Tooltip>
  );

  return (
    <div
      className={`${rootClass} ${pulse ? 'lives-ui--pulse' : ''} ${className}`.trim()}
      aria-label={t('user.lives', 'Lives')}
    >
      <div className="lives-ui-label-row">
        <small className="text-muted lives-ui-title">{t('user.lives', 'Lives')}</small>
        {unlimited && (
          <span className="badge bg-secondary lives-ui-badge-unlimited">
            {t('user.livesUnlimited', 'Unlimited')}
          </span>
        )}
      </div>
      <div className="lives-ui-hearts-row">
        <div className="lives-ui-hearts">
          {slots.map((state, i) => (
            <HeartSlot key={i} state={state} />
          ))}
        </div>
        {showVipX2 && (
          <OverlayTrigger placement="top" trigger={['hover', 'focus']} overlay={x2Tooltip}>
            <PulseBadge variant="orange" size="md" className="lives-ui-vip-x2" tabIndex={0}>
              2x
            </PulseBadge>
          </OverlayTrigger>
        )}
      </div>
      {!unlimited && countdownLabel != null && (
        <div className="lives-ui-countdown text-muted" role="timer" aria-live="polite">
          <i className="bi bi-clock me-1" aria-hidden />
          <span>
            {t('user.nextLifeIn', 'Next in {{time}}').replace('{{time}}', countdownLabel)}
          </span>
        </div>
      )}
    </div>
  );
};

export default LivesDisplay;
